import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  ArrowLeft,
  Target,
  Calendar,
  BookOpen,
  Brain,
  Zap,
  CheckCircle2,
  Clock,
  Sparkles,
  ChevronRight,
  PlayCircle,
  Trophy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LearningPlan = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    loadPlan();
  }, []);

  const loadPlan = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load plan and capabilities in parallel
      const [planRes, capRes] = await Promise.all([
        axios.get(`${API}/learning-plan/current`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/student/capabilities`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPlan(planRes.data.plan);
      setCapabilities(capRes.data);
      
      // Find today's day
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
      const dayIndex = planRes.data.plan?.dailyGoals?.findIndex(d => d.day === today);
      if (dayIndex >= 0) setActiveDay(dayIndex);
      
    } catch (error) {
      console.error('Error loading plan:', error);
    }
    setLoading(false);
  };

  const getBandColor = (band) => {
    switch (band) {
      case 'stretch': return 'bg-green-100 text-green-700 border-green-300';
      case 'secure': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'foundation': return 'bg-amber-100 text-amber-700 border-amber-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getModuleIcon = (type) => {
    switch (type) {
      case 'core_concepts': return <BookOpen className="h-5 w-5" />;
      case 'practice': return <Target className="h-5 w-5" />;
      case 'targeted_practice': return <Zap className="h-5 w-5" />;
      case 'challenge': return <Trophy className="h-5 w-5" />;
      default: return <Brain className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-purple-600">Loading your learning plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 md:p-8">
        <div className="max-w-2xl mx-auto text-center">
          <Brain className="h-16 w-16 text-purple-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">No Learning Plan Yet</h1>
          <p className="text-gray-600 mb-6">
            Complete a diagnostic assessment to get your personalized learning plan.
          </p>
          <Button
            onClick={() => navigate('/categories')}
            className="bg-gradient-to-r from-purple-600 to-indigo-600"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                <Sparkles className="h-8 w-8 text-amber-500" />
                This Week's Plan
              </h1>
              <p className="text-gray-600 mt-1">Week {plan.weekNumber} • Personalized for you</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Daily Goals */}
          <div className="lg:col-span-2 space-y-4">
            {/* Day Selector */}
            <Card className="shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="h-5 w-5 text-purple-600" />
                  Daily Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {plan.dailyGoals?.map((day, index) => (
                    <button
                      key={day.day}
                      onClick={() => setActiveDay(index)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                        activeDay === index
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      } ${day.completed ? 'ring-2 ring-green-400' : ''}`}
                    >
                      {day.day.slice(0, 3)}
                      {day.completed && <CheckCircle2 className="h-3 w-3 ml-1 inline" />}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Today's Tasks */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">{plan.dailyGoals?.[activeDay]?.day}'s Tasks</CardTitle>
                <CardDescription>
                  Focus: {plan.dailyGoals?.[activeDay]?.subjects?.join(', ')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {plan.dailyGoals?.[activeDay]?.tasks?.map((task, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-4 p-4 bg-white border rounded-xl hover:shadow-md transition-shadow"
                  >
                    <Checkbox />
                    <div className="flex-1">
                      <p className="font-medium capitalize">
                        {task.type.replace('_', ' ')} - {task.subject.replace('-', ' ')}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {task.duration} minutes
                      </p>
                    </div>
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                      <PlayCircle className="h-4 w-4 mr-1" />
                      Start
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Subject Modules */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BookOpen className="h-5 w-5 text-purple-600" />
                  Learning Modules
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(plan.subjects || {}).map(([subject, data]) => (
                  <div key={subject} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold capitalize text-gray-800">
                        {subject.replace('-', ' ')}
                      </h3>
                      <Badge variant="outline" className={getBandColor(data.band)}>
                        {data.band}
                      </Badge>
                    </div>
                    
                    <div className="grid gap-2">
                      {data.modules?.map((module, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          <div className={`p-2 rounded-lg ${
                            module.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'
                          }`}>
                            {getModuleIcon(module.type)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{module.name}</p>
                            <p className="text-xs text-gray-500">{module.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">{module.estimatedTime} min</p>
                            {module.status === 'completed' ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Progress & Capabilities */}
          <div className="space-y-4">
            {/* Capability Summary */}
            {capabilities?.capabilityBands && (
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Target className="h-5 w-5 text-purple-600" />
                    Your Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(capabilities.capabilityBands).map(([subject, data]) => (
                    <div key={subject} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="capitalize font-medium">{subject.replace('-', ' ')}</span>
                        <Badge variant="outline" className={getBandColor(data.band)}>
                          {data.score}%
                        </Badge>
                      </div>
                      <Progress value={data.score} className="h-2" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Quick Tips */}
            <Card className="shadow-lg bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-amber-800">
                  <Zap className="h-5 w-5" />
                  Study Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <span className="text-amber-600">💡</span>
                  <p className="text-sm text-amber-900">Start with subjects you find challenging while your mind is fresh.</p>
                </div>
                <div className="flex gap-2">
                  <span className="text-amber-600">⏰</span>
                  <p className="text-sm text-amber-900">Take a 5-minute break every 25 minutes of study.</p>
                </div>
                <div className="flex gap-2">
                  <span className="text-amber-600">🎯</span>
                  <p className="text-sm text-amber-900">Review your weak areas at least twice per week.</p>
                </div>
              </CardContent>
            </Card>

            {/* Action Card */}
            <Card className="shadow-lg">
              <CardContent className="pt-6">
                <Button 
                  onClick={() => navigate('/categories')}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Start Today's Practice
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningPlan;
